import { useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/AuthContext.jsx';
import { Avatar, useToast } from './ui.jsx';
import Icon from './Icon.jsx';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 3 * 1024 * 1024;

/**
 * Profile-photo control shared by every role. Uploads the raw image, then
 * refreshes the session user so the new avatar shows everywhere at once.
 */
export default function AvatarUpload() {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const pick = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      return toast.error('Upload a JPG, PNG or WebP image.');
    }
    if (file.size > MAX_BYTES) {
      return toast.error('That image is over the 3 MB limit.');
    }
    setBusy(true);
    try {
      await api.upload('/api/me/avatar', file);
      await refresh();
      toast.success('Profile photo updated.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del('/api/me/avatar');
      await refresh();
      toast.success('Profile photo removed.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="avatar-upload">
      <button
        type="button"
        className="avatar-upload-target"
        onClick={() => inputRef.current?.click()}
        aria-label="Change profile photo"
        disabled={busy}
      >
        <Avatar name={user.name} avatar={user.avatar} size="lg" />
        <span className="avatar-upload-overlay">
          {busy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Icon name="camera" />}
        </span>
      </button>
      <div className="stack-sm">
        <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()} disabled={busy}>
          <Icon name="upload" />
          {user.avatar ? 'Change photo' : 'Upload photo'}
        </button>
        {user.avatar && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={remove} disabled={busy}>
            <Icon name="trash" /> Remove
          </button>
        )}
        <span className="tiny dim">JPG, PNG or WebP · up to 3 MB</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
